// src/components/Explore/ClubProgramTile.jsx
// Club tile with embedded programs list (Variant B - Expandable)

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaStar, 
  FaMapMarkerAlt, 
  FaUsers,
  FaChevronDown,
  FaChevronUp,
  FaArrowRight,
  FaCrown,
  FaUser,
  FaFutbol,
  FaEnvelope,
  FaClock
} from 'react-icons/fa';
import ProgramRow from './ProgramRow';
import styles from './ClubProgramTile.module.css';

const ClubProgramTile = ({ 
  club, 
  capacityMap = {},
  userMembership = null,
  defaultExpanded = false 
}) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [imageError, setImageError] = useState(false);

  // Filter to only show active programs
  const activePrograms = useMemo(() => {
    const programs = club.programs || [];
    return programs.filter(program => program.active !== false);
  }, [club.programs]);

  const hasPrograms = activePrograms.length > 0;
  const visiblePrograms = isExpanded ? activePrograms : activePrograms.slice(0, 2);
  const hasMorePrograms = activePrograms.length > 2;

  // Handle navigation to club page
  const handleViewClub = (e) => {
    e.stopPropagation();
    navigate(`/club/${club._id}`);
  };

  // Handle club name click
  const handleClubNameClick = (e) => {
    e.stopPropagation();
    navigate(`/club/${club._id}`);
  };

  // Toggle expansion
  const handleToggle = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Handle image error - show fallback
  const handleImageError = () => {
    setImageError(true);
  };

  // Membership badge
  const getMembershipBadge = () => {
    if (!userMembership) return null;

    const role = userMembership.role?.toLowerCase();
    const status = userMembership.status?.toLowerCase();
    
    // Don't show member badge for invited users - they haven't accepted yet
    if (status === 'invited') {
      return (
        <div className={`${styles.membershipBadge} ${styles.pendingBadge}`}>
          <FaEnvelope className={styles.badgeIcon} />
          <span>Invited</span>
        </div>
      );
    }

    // Don't show member badge for pending users
    if (status === 'pending') {
      return (
        <div className={`${styles.membershipBadge} ${styles.pendingBadge}`}>
          <FaClock className={styles.badgeIcon} />
          <span>Pending</span>
        </div>
      );
    }

    // Only show membership badges for active members (not invited, pending, rejected, etc.)
    // If status exists but is not 'active', 'approved', or 'member', don't show Member badge
    const isActiveMember = !status || status === 'active' || status === 'approved' || status === 'member';
    if (!isActiveMember) {
      return null;
    }
    
    if (role === 'creator') {
      return (
        <div className={`${styles.membershipBadge} ${styles.creatorBadge}`}>
          <FaCrown className={styles.badgeIcon} />
          <span>Creator</span>
        </div>
      );
    }
    
    if (['owner', 'manager', 'admin'].includes(role)) {
      return (
        <div className={`${styles.membershipBadge} ${styles.managerBadge}`}>
          <FaCrown className={styles.badgeIcon} />
          <span>{userMembership.role}</span>
        </div>
      );
    }

    return (
      <div className={styles.membershipBadge}>
        <FaUser className={styles.badgeIcon} />
        <span>Member</span>
      </div>
    );
  };

  // Determine if we should show image or fallback
  const showFallback = imageError || !club.clubLogo;

  return (
    <div className={styles.clubTile}>
      {/* Club Header */}
      <div className={styles.clubHeader}>
        <div className={styles.logoWrapper}>
          {showFallback ? (
            <div className={styles.logoFallback}>
              <FaFutbol className={styles.fallbackIcon} />
            </div>
          ) : (
            <img
              src={club.clubLogo}
              alt={`${club.name} logo`}
              className={styles.clubLogo}
              onError={handleImageError}
            />
          )}
        </div>

        <div className={styles.clubInfo}>
          {/* Header Row with Name, Badge, and View Button */}
          <div className={styles.headerRow}>
            <h3 
              className={styles.clubName}
              onClick={handleClubNameClick}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && handleClubNameClick(e)}
            >
              {club.name}
            </h3>
            {getMembershipBadge()}
            <button 
              className={styles.viewClubButton}
              onClick={handleViewClub}
            >
              View Club <FaArrowRight className={styles.arrowIcon} />
            </button>
          </div>
          
          <div className={styles.clubMeta}>
            {club.rating !== undefined && club.rating !== null && (
              <span className={styles.metaItem}>
                <FaStar className={styles.starIcon} />
                {Number(club.rating).toFixed(1)}
              </span>
            )}
            
            {club.distance !== null && club.distance !== undefined && (
              <span className={styles.metaItem}>
                <FaMapMarkerAlt className={styles.metaIcon} />
                {club.distance} mi
              </span>
            )}
            
            {club.memberCount && (
              <span className={styles.metaItem}>
                <FaUsers className={styles.metaIcon} />
                {club.memberCount}
              </span>
            )}
          </div>

          {club.description && (
            <p className={styles.clubDescription}>
              {club.description.length > 100 
                ? `${club.description.substring(0, 100)}...` 
                : club.description
              }
            </p>
          )}
        </div>
      </div>

      {/* Programs Section */}
      {hasPrograms && (
        <>
          <div className={styles.programsDivider}>
            <span className={styles.programsCount}>
              📋 {activePrograms.length} Program{activePrograms.length !== 1 ? 's' : ''} Available
            </span>
          </div>

          <div className={styles.programsList}>
            {visiblePrograms.map((program) => (
              <ProgramRow
                key={program._id}
                program={program}
                capacityStatus={capacityMap[program._id]}
                clubId={club._id}
              />
            ))}
          </div>

          {/* Expand/Collapse Button */}
          {hasMorePrograms && (
            <button 
              className={styles.toggleButton}
              onClick={handleToggle}
            >
              {isExpanded ? (
                <>
                  <FaChevronUp className={styles.chevron} />
                  Show Less
                </>
              ) : (
                <>
                  <FaChevronDown className={styles.chevron} />
                  Show {activePrograms.length - 2} More Program{activePrograms.length - 2 !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </>
      )}

      {/* No Programs State */}
      {!hasPrograms && (
        <div className={styles.noProgramsState}>
          <span className={styles.noProgramsText}>
            No active programs available
          </span>
        </div>
      )}
    </div>
  );
};

export default ClubProgramTile;










